import {
	useIsMutating,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { toast } from "sonner";
import { entryGroupsQueryKey } from "@/shared/cache";
import { timerSpansQueryKey } from "@/shared/config/queryKeys";
import type { ApiResponse } from "@/shared/types/request";
import { useApi } from "@/shared/utils/request";
import { timerDisplaySeconds, timerDisplayUnits } from "@/v2/lib/timerDisplay";

/**
 * A smart timer routes captured work into its group and adds elapsed time to
 * the group's saved billable units. The server owns start/pause and persistence;
 * the browser derives the live display from that span, including after reload.
 */
export interface TimerSpan {
	object: "timer_span";
	id: string;
	entryGroupId: string;
	status: "running" | "completed";
	startingDurationSeconds: number;
	billingIncrementSeconds: number;
	endingDurationSeconds: number | null;
	/** Raw capture baseline used by the server to reconcile Other tasks. */
	startingActualDurationSeconds: number;
	currentDurationSeconds: number;
	startedAt: string;
	endedAt: string | null;
	createdAt: string;
	modifiedAt: string;
}

interface TimerSpanListWire {
	object: "list";
	url: string;
	hasMore: boolean;
	data: TimerSpan[];
}

const TIMER_QUERY_ERROR_TOAST_ID = "smart-timer-query-error";

/**
 * The browser's local wall-clock as a naive `YYYY-MM-DDTHH:mm:ss` string, sent so
 * the timer's entry reads in the attorney's timezone (like captured entries).
 */
function localWallClockNow(): string {
	const d = new Date();
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
		d.getHours(),
	)}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function timerErrorMessage(res: ApiResponse, fallback: string): string {
	if (res.apiError?.code === "TIMER_GROUP_INELIGIBLE") {
		return (
			res.apiError.message ||
			"This group can no longer run a timer — it may have been exported or deleted."
		);
	}
	return res.error || fallback;
}

interface SmartTimerContextValue {
	/** Group with the running timer (optimistic during start/switch). Null when idle. */
	activeEntryGroupId: string | null;
	/** Target of an in-flight start/switch, for a pending spinner on that row. */
	pendingTargetId: string | null;
	/** Group whose timer is being paused, for a pending spinner while it winds down. */
	pausingEntryGroupId: string | null;
	/** True while any start/switch/pause request is in flight — blocks duplicates. */
	isBusy: boolean;
	/** Blocks start/switch until pending entry edits are saved; pause stays available. */
	isSavingEntries: boolean;
	/** True until the first timer fetch resolves. */
	isLoading: boolean;
	/** Start a timer on this group, or switch the single running timer to it. */
	startOrSwitch: (entryGroupId: string) => void;
	/** Start a timer on a brand-new empty entry group (created server-side). */
	startNew: () => void;
	/** True while the new-empty-group timer is being created. */
	isStartingNew: boolean;
	/** Pause the running timer, restoring automatic prediction/grouping. */
	pause: () => void;
}

const SmartTimerContext = createContext<SmartTimerContextValue | undefined>(
	undefined,
);

/**
 * Live display seconds (start base + elapsed), re-derived each second in its OWN
 * context so the tick re-renders only the toolbar clock, not the entries table.
 * 0 when idle.
 */
const SmartTimerDisplayContext = createContext<number>(0);
const SmartTimerUnitsContext = createContext<number>(0);

export function SmartTimerProvider({ children }: { children: ReactNode }) {
	const parent = useContext(SmartTimerContext);
	// Idempotent: a nested provider defers to the outer one so the timer stays a
	// single source of truth even if the tree double-wraps.
	if (parent) return <>{children}</>;
	return <SmartTimerProviderRoot>{children}</SmartTimerProviderRoot>;
}

function SmartTimerProviderRoot({ children }: { children: ReactNode }) {
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();
	const pendingEntryWrites = useIsMutating({
		mutationKey: entryGroupsQueryKey,
	});

	// Refresh the entries table (new group, "Other tasks" fill, floated order).
	const invalidateEntryGroups = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: entryGroupsQueryKey });
	}, [queryClient]);

	// Abort an in-flight timer-spans fetch before mutating: otherwise a focus- or
	// socket-triggered GET that started before the mutation can resolve afterwards
	// and clobber the optimistic setRunningSpan with a stale value.
	const cancelTimerSpans = useCallback(
		() => queryClient.cancelQueries({ queryKey: timerSpansQueryKey }),
		[queryClient],
	);

	// After a timer mutation, refresh both the table and the authoritative span so
	// this tab reconciles immediately; the "timer_changed" socket push reconciles
	// the user's other tabs/devices.
	const settleTimerMutation = useCallback(() => {
		invalidateEntryGroups();
		void queryClient.invalidateQueries({ queryKey: timerSpansQueryKey });
	}, [invalidateEntryGroups, queryClient]);

	// Write the running span into the cache from the mutation result so the toolbar
	// flips instantly instead of waiting for a refetch (that lag was the pause delay
	// and start flicker). The socket push reconciles the other tabs in the background.
	const setRunningSpan = useCallback(
		(span: TimerSpan | null) => {
			queryClient.setQueryData(timerSpansQueryKey, span);
		},
		[queryClient],
	);

	const query = useQuery({
		queryKey: timerSpansQueryKey,
		queryFn: async ({ signal }) => {
			const res = await makeApiRequest({
				endpoint: "/v2/timer-spans",
				method: "GET",
				otherOptions: { signal },
			});
			if (res.status === "ERROR")
				throw new Error(res.error || "Couldn't load the active timer.");
			const body = res.data as TimerSpanListWire | undefined;
			return body?.data?.find((span) => span.status === "running") ?? null;
		},
		// Convergence across tabs/devices is push-driven (the "timer_changed" socket
		// event invalidates this key), so there's no polling interval. Focus refetch
		// stays on as a cheap backstop for a tab that missed a push while hidden; the
		// global default is off, and staleTime is registered centrally in queryClient.
		refetchOnWindowFocus: true,
	});

	useEffect(() => {
		if (query.error) {
			toast.error(
				query.error instanceof Error
					? query.error.message
					: "Couldn't load the active timer.",
				{ id: TIMER_QUERY_ERROR_TOAST_ID },
			);
			return;
		}
		toast.dismiss(TIMER_QUERY_ERROR_TOAST_ID);
	}, [query.error]);

	const startMutation = useMutation({
		mutationKey: [...timerSpansQueryKey, "start"],
		mutationFn: async (entryGroupId: string) => {
			const res = await makeApiRequest({
				endpoint: "/v2/timer-spans",
				method: "POST",
				body: { entryGroupId, clientTime: localWallClockNow() },
			});
			if (res.status === "ERROR")
				throw new Error(timerErrorMessage(res, "Couldn't start the timer."));
			return res.data as TimerSpan;
		},
		onMutate: cancelTimerSpans,
		// Reflect the new running span immediately, then refresh the table.
		onSuccess: (span) => setRunningSpan(span),
		onError: (error) =>
			toast.error(
				error instanceof Error ? error.message : "Couldn't start the timer.",
			),
		onSettled: settleTimerMutation,
	});

	// Empty-body POST: the server creates an empty `tracked` group AND its running
	// span in one call, so we never orphan a group if a second request fails.
	const startNewMutation = useMutation({
		mutationKey: [...timerSpansQueryKey, "start-new"],
		mutationFn: async () => {
			const res = await makeApiRequest({
				endpoint: "/v2/timer-spans",
				method: "POST",
				body: { clientTime: localWallClockNow() },
			});
			if (res.status === "ERROR")
				throw new Error(timerErrorMessage(res, "Couldn't start the timer."));
			return res.data as TimerSpan;
		},
		onMutate: cancelTimerSpans,
		onSuccess: (span) => setRunningSpan(span),
		onError: (error) =>
			toast.error(
				error instanceof Error ? error.message : "Couldn't start the timer.",
			),
		onSettled: settleTimerMutation,
	});

	const pauseMutation = useMutation({
		mutationKey: [...timerSpansQueryKey, "pause"],
		mutationFn: async (spanId: string) => {
			const res = await makeApiRequest({
				endpoint: `/v2/timer-spans/${spanId}`,
				method: "PATCH",
				body: { status: "completed", clientTime: localWallClockNow() },
			});
			if (res.status === "ERROR")
				throw new Error(timerErrorMessage(res, "Couldn't pause the timer."));
			return res.data as TimerSpan;
		},
		onMutate: cancelTimerSpans,
		// The span is now completed — clear the running span immediately so the
		// toolbar stops without waiting for a refetch.
		onSuccess: () => setRunningSpan(null),
		onError: (error) =>
			toast.error(
				error instanceof Error ? error.message : "Couldn't pause the timer.",
			),
		onSettled: settleTimerMutation,
	});

	// ── Live "keep time" display ──────────────────────────────────────────────
	// Elapsed derives from the server span's startedAt, so every tab agrees and a
	// reload resumes.
	const runningSpan = query.data;
	const startedAtMs = runningSpan ? Date.parse(runningSpan.startedAt) : null;
	const [nowMs, setNowMs] = useState(() => Date.now());
	useEffect(() => {
		if (startedAtMs === null) return;
		setNowMs(Date.now());
		const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
		// Snap immediately when the tab regains focus (intervals throttle when hidden).
		const onVisible = () => {
			if (document.visibilityState === "visible") setNowMs(Date.now());
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			window.clearInterval(interval);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [startedAtMs]);
	const displaySeconds = timerDisplaySeconds(runningSpan, nowMs);
	const displayUnits = timerDisplayUnits(runningSpan, nowMs);

	const serverActiveId = query.data?.entryGroupId ?? null;
	const serverActiveSpanId = query.data?.id ?? null;
	const startTarget = startMutation.isPending
		? (startMutation.variables ?? null)
		: null;
	const isPausing = pauseMutation.isPending;
	const isBusy =
		startMutation.isPending ||
		pauseMutation.isPending ||
		startNewMutation.isPending;

	// Optimistic truth: an in-flight start/switch arms its target immediately (and
	// un-arms the previous group); an in-flight pause un-arms right away. The next
	// refetch reconciles with the server, which always wins.
	const activeEntryGroupId = isPausing ? null : (startTarget ?? serverActiveId);
	const pausingEntryGroupId = isPausing ? serverActiveId : null;

	const startOrSwitch = useCallback(
		(entryGroupId: string) => {
			if (
				isBusy ||
				queryClient.isMutating({ mutationKey: entryGroupsQueryKey })
			)
				return;
			startMutation.mutate(entryGroupId);
		},
		[isBusy, queryClient, startMutation],
	);

	const pause = useCallback(() => {
		if (isBusy || !serverActiveSpanId) return;
		pauseMutation.mutate(serverActiveSpanId);
	}, [isBusy, pauseMutation, serverActiveSpanId]);

	const startNew = useCallback(() => {
		if (isBusy || queryClient.isMutating({ mutationKey: entryGroupsQueryKey }))
			return;
		startNewMutation.mutate();
	}, [isBusy, queryClient, startNewMutation]);

	const value = useMemo<SmartTimerContextValue>(
		() => ({
			activeEntryGroupId,
			pendingTargetId: startTarget,
			pausingEntryGroupId,
			isBusy,
			isSavingEntries: pendingEntryWrites > 0,
			isLoading: query.isLoading,
			startOrSwitch,
			startNew,
			isStartingNew: startNewMutation.isPending,
			pause,
		}),
		[
			activeEntryGroupId,
			startTarget,
			pausingEntryGroupId,
			isBusy,
			pendingEntryWrites,
			query.isLoading,
			startOrSwitch,
			startNew,
			startNewMutation.isPending,
			pause,
		],
	);

	return (
		<SmartTimerContext.Provider value={value}>
			<SmartTimerDisplayContext.Provider value={displaySeconds}>
				<SmartTimerUnitsContext.Provider value={displayUnits}>
					{children}
				</SmartTimerUnitsContext.Provider>
			</SmartTimerDisplayContext.Provider>
		</SmartTimerContext.Provider>
	);
}

export function useSmartTimer(): SmartTimerContextValue {
	const context = useContext(SmartTimerContext);
	if (!context)
		throw new Error("useSmartTimer must be used within a SmartTimerProvider");
	return context;
}

/**
 * Live base+elapsed seconds (ticks each second). Consume only where the clock is
 * shown, to keep the tick's re-renders off the rest of the tree.
 */
export function useSmartTimerDisplaySeconds(): number {
	return useContext(SmartTimerDisplayContext);
}

export function useSmartTimerDisplayUnits(): number {
	return useContext(SmartTimerUnitsContext);
}
